import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { sendMessage } from '../api';
import InsufficientCreditsModal from './InsufficientCreditsModal';
import ReactMarkdown from 'react-markdown';

function ChatBot() {
  const { user, deductCredits } = useAuth();
  const navigate = useNavigate();
  
  const effectRan = useRef(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Enhanced state management with response length preference
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isFirstQuestion, setIsFirstQuestion] = useState(true);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);
  const [requiredCredits, setRequiredCredits] = useState(0);
  const [draftMessage, setDraftMessage] = useState('');
  const [shortResponse, setShortResponse] = useState(true); // ✅ New: Response length preference

  // Load chat history from localStorage
  const loadChatHistory = () => {
    if (!user?.id) return [];
    
    const savedMessages = localStorage.getItem(`astroguru_chat_${user.id}`);
    if (savedMessages) {
      try {
        return JSON.parse(savedMessages);
      } catch (error) {
        console.error('Error loading chat history:', error);
      }
    }
    return [];
  };

  // Load first question status from localStorage
  const loadFirstQuestionStatus = () => {
    if (!user?.id) return true;
    
    const savedStatus = localStorage.getItem(`astroguru_first_question_${user.id}`);
    return savedStatus ? JSON.parse(savedStatus) : true;
  };

  // ✅ Load short response preference from localStorage
  const loadResponsePreference = () => {
    if (!user?.id) return true;
    
    const savedPreference = localStorage.getItem(`astroguru_short_response_${user.id}`);
    return savedPreference ? JSON.parse(savedPreference) : true;
  };

  // Load draft message from localStorage
  const loadDraftMessage = () => {
    if (!user?.id) return '';
    
    const savedDraft = localStorage.getItem(`astroguru_draft_${user.id}`);
    return savedDraft || '';
  };

  // Initialize messages and preferences when user is available
  useEffect(() => {
    if (user?.id && !hasInitialized) {
      const savedMessages = loadChatHistory();
      const savedFirstQuestion = loadFirstQuestionStatus();
      const savedDraft = loadDraftMessage();
      const savedResponsePreference = loadResponsePreference();
      
      setMessages(savedMessages);
      setIsFirstQuestion(savedFirstQuestion);
      setDraftMessage(savedDraft);
      setInputValue(savedDraft);
      setShortResponse(savedResponsePreference); // ✅ Load preference
      setHasInitialized(true);
      
      console.log('📚 Loaded chat data:', { 
        messagesCount: savedMessages.length, 
        isFirstQuestion: savedFirstQuestion,
        hasDraft: !!savedDraft,
        shortResponse: savedResponsePreference
      });
    }
  }, [user?.id, hasInitialized]);

  // Save messages to localStorage whenever messages change
  useEffect(() => {
    if (user?.id && messages.length > 0) {
      localStorage.setItem(`astroguru_chat_${user.id}`, JSON.stringify(messages));
    }
  }, [messages, user?.id]);

  // Save first question status whenever it changes
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`astroguru_first_question_${user.id}`, JSON.stringify(isFirstQuestion));
    }
  }, [isFirstQuestion, user?.id]);

  // ✅ Save response preference whenever it changes
  useEffect(() => {
    if (user?.id) {
      localStorage.setItem(`astroguru_short_response_${user.id}`, JSON.stringify(shortResponse));
    }
  }, [shortResponse, user?.id]);

  // Save draft message whenever input changes
  useEffect(() => {
    if (user?.id) {
      setDraftMessage(inputValue);
      localStorage.setItem(`astroguru_draft_${user.id}`, inputValue);
    }
  }, [inputValue, user?.id]);

  // Enhanced auto-welcome trigger
  useEffect(() => {
    if (
      user?.id && 
      hasInitialized && 
      messages.length === 0 && 
      effectRan.current === false
    ) {
      console.log('🎯 Running initial welcome and horoscope (should only happen once)');
      effectRan.current = true;
      
      setTimeout(() => {
        showWelcomeAndHoroscope();
      }, 100);
    }
  }, [user?.id, hasInitialized, messages.length]);

  const push = msg => setMessages(prev => [...prev, msg]);

  // Enhanced Guru messages with more mystical variety
  const getRandomGuruMessage = () => {
    const messages = [
      "🔮 Guru ji is consulting the ancient Vedic texts... ✨",
      "🌟 Guru ji is reading your celestial birth chart... 🌙", 
      "⭐ Guru ji is analyzing your cosmic energy patterns... 🔥",
      "🧘 Guru ji is meditating on your divine question... 🌸",
      "📜 Guru ji is studying your planetary positions... 🪐",
      "🕉️ Guru ji is channeling ancient wisdom for you... ✨",
      "🌺 Guru ji is connecting with universal consciousness... 🌌",
      "🔱 Guru ji is interpreting the sacred cosmic signs... 🌟",
      "🪷 Guru ji is seeking guidance from the divine cosmos... ⭐",
      "🌸 Guru ji is unraveling your karmic destiny threads... 🧵",
    ];
    
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // ✅ Enhanced user profile context with response length instruction
  const getUserProfileContext = () => {
    if (!user) return '';

    const birthDate = user.birth_date ? new Date(user.birth_date) : null;
    const birthTime = user.birth_time ? new Date(user.birth_time) : null;
    
    const formattedBirthDate = birthDate ? 
      `${birthDate.getDate()}/${birthDate.getMonth() + 1}/${birthDate.getFullYear()}` : 
      'Not provided';
    
    const formattedBirthTime = birthTime ? 
      `${birthTime.getHours().toString().padStart(2, '0')}:${birthTime.getMinutes().toString().padStart(2, '0')}` : 
      'Not provided';

    const age = birthDate ? 
      Math.floor((new Date() - birthDate) / (365.25 * 24 * 60 * 60 * 1000)) : 
      'Unknown';

    // ✅ Response length instruction based on user preference
    const responseLength = shortResponse ? 
      "Keep your response SHORT, SIMPLE, and EASY TO UNDERSTAND (maximum 40-60 words). Use simple language that anyone can understand. Focus on one main point with clear, actionable advice." :
      "Provide a detailed, comprehensive response with thorough explanations and multiple insights.";
    
    return `You are Guru ji, a wise and compassionate astrologer with 40+ years of experience in Vedic astrology. Speak in a warm, paternal tone like a caring Indian guru. Use phrases like "beta", "my child", "according to the stars", "the cosmos reveals", "blessings be upon you".

IMPORTANT RESPONSE STYLE: ${responseLength}

Sacred devotee profile for personalized guidance:
🙏 Full Name: ${user.full_name || user.name || 'Divine Soul'}
📧 Email: ${user.email || 'Not provided'}
🎂 Birth Date: ${formattedBirthDate} (Age: ${age} years)
⏰ Birth Time: ${formattedBirthTime}
📍 Birth Place: ${user.birth_place || 'Not provided'}
🌍 Current Timezone: ${user.timezone || 'Asia/Kolkata'}
👤 Given Name: ${user.given_name || 'Beta'}
👨‍👩‍👧‍👦 Family Name: ${user.family_name || ''}
⭐ Member Since: ${user.member_since ? new Date(user.member_since).toLocaleDateString() : 'Recently joined'}
🔄 Last Login: ${user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Today'}

Based on this sacred birth information and cosmic data, respond as Guru ji with deep wisdom, compassion, and personalized astrological guidance. Address them affectionately and provide insights based on their birth details.`;
  };

  // Enhanced welcome message with user's actual name (NO CREDIT DEDUCTION)
  const showWelcomeAndHoroscope = async () => {
    console.log('🌟 Adding personalized welcome message (FREE)');
    
    const userName = user?.full_name || user?.given_name || user?.name || 'seeker';
    
    push({
      message: `**Namaste ${userName}! ✨ Welcome to your cosmic sanctuary.** The stars have been eagerly waiting for your arrival, my child...`,
      direction: 'incoming',
      sender: 'assistant',
      timestamp: new Date().toISOString()
    });

    setTimeout(() => {
      loadTodaysHoroscope();
    }, 1500);
  };

  // Enhanced horoscope loading with user's birth data (NO CREDIT DEDUCTION)
  const loadTodaysHoroscope = async () => {
    console.log('🔮 Loading personalized horoscope with birth data (FREE)');
    
    const userName = user?.given_name || user?.name?.split(' ')[0] || 'beta';

    setIsTyping(true);

    const horoscopeQuery = getUserProfileContext() + `Please provide today's personalized horoscope for ${user.given_name || 'this devotee'} in a SHORT, SIMPLE format (30-40 words maximum). Consider their birth date (${user.birth_date}), birth time (${user.birth_time}), and birth place (${user.birth_place}) for accurate cosmic insights. Keep it positive, actionable, and address them as 'beta' or 'my child'. Focus on one main astrological insight for today based on their birth chart.`;
    
    console.log('🔮 Auto-loading personalized horoscope (FREE) with complete profile context');

    try {
      const { data } = await sendMessage(horoscopeQuery);
      
      setTimeout(() => {
        push({
          message: `**Today's personalized horoscope reveals:** ${data.reply}`,
          direction: 'incoming',
          sender: 'assistant',
          timestamp: new Date().toISOString()
        });
      }, 1000);
      
    } catch (err) {
      setTimeout(() => {
        push({
          message: `**Today's cosmic guidance for ${userName}:** The celestial energies are particularly favorable for you today, my child. Your birth chart indicates strong planetary support for new beginnings and spiritual growth. Trust in the divine timing! 🌟✨`,
          direction: 'incoming',
          sender: 'assistant',
          timestamp: new Date().toISOString()
        });
      }, 1000);
      console.error('Horoscope loading error:', err);
    } finally {
      setTimeout(() => {
        setIsTyping(false);
      }, 800);
    }
  };

  // Clear draft when message is successfully sent
  const clearDraft = () => {
    if (user?.id) {
      setDraftMessage('');
      localStorage.removeItem(`astroguru_draft_${user.id}`);
    }
  };

  // ✅ Enhanced message handling with response length preference
  async function handleSend() {
    if (!inputValue.trim()) return;

    const creditsNeeded = isFirstQuestion ? 10 : 5;
    
    if (user.credits < creditsNeeded) {
      setRequiredCredits(creditsNeeded);
      setShowInsufficientModal(true);
      console.log('💰 Insufficient credits - keeping draft:', inputValue);
      return;
    }

    const userMessage = {
      message: inputValue,
      direction: 'outgoing',
      sender: 'user',
      timestamp: new Date().toISOString()
    };
    push(userMessage);

    // ✅ Enhanced message with response length instruction
    const fullMessageWithProfile = getUserProfileContext() + `User's question: "${inputValue}"\n\nPlease provide a ${shortResponse ? 'SHORT, SIMPLE, and EASY TO UNDERSTAND' : 'detailed and comprehensive'} astrological response as Guru ji, considering their complete birth details and cosmic profile. Keep the response warm, wise, and spiritually enriching.${shortResponse ? ' Maximum 50-60 words, focus on one main insight with clear advice.' : ''}`;
    
    console.log('🔮 Sending to API with enhanced profile context and response length preference:', shortResponse ? 'SHORT' : 'DETAILED');
    console.log('👤 User question:', inputValue);

    setInputValue('');
    clearDraft();
    setIsTyping(true);

    deductCredits(creditsNeeded);
    setIsFirstQuestion(false);

    try {
      const { data } = await sendMessage(fullMessageWithProfile);
      push({ 
        message: data.reply, 
        direction: 'incoming', 
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      const userName = user?.given_name || user?.name?.split(' ')[0] || 'beta';
      push({
        message: `Forgive me ${userName}, the cosmic connection seems disturbed at this moment. The divine energies are temporarily realigning. Please try again in a moment, my child. **Om Shanti...** 🙏✨`,
        direction: 'incoming',
        sender: 'assistant',
        timestamp: new Date().toISOString()
      });
      console.error('Chat error:', err);
    } finally {
      setIsTyping(false);
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  const handleGetCredits = () => {
    console.log('📍 Going to credits page, draft preserved:', inputValue);
    navigate('/credits');
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleCloseInsufficientModal = () => {
    setShowInsufficientModal(false);
    console.log('💬 Modal closed, draft preserved:', inputValue);
  };

  // ✅ Toggle response length preference
  const toggleResponseLength = () => {
    setShortResponse(!shortResponse);
    console.log('📝 Response length changed to:', !shortResponse ? 'SHORT' : 'DETAILED');
  };

  const handleFreeKundli = () => {
    console.log('🔮 Free Kundli clicked for:', user?.full_name);
  };
  
  const handleBookPooja = () => {
    console.log('📖 Book Pooja clicked for:', user?.full_name);
  };
  
  const handleMoonTracker = () => {
    console.log('🌙 Moon Tracker clicked for:', user?.full_name);
  };

  return (
    <div className="chatbot-container">
      {/* Credit Warning Banner */}
      {user?.credits < 10 && (
        <div className="credit-warning" onClick={handleGetCredits}>
          <span>⚠️ Low Credits! You have only {user?.credits} credits left, {user?.given_name || 'beta'}. Click to get more! 💎</span>
        </div>
      )}

      {/* Enhanced Draft Indicator */}
      {draftMessage && draftMessage.trim() !== '' && (
        <div className="draft-indicator">
          <span>📝 Draft saved for {user?.given_name || 'you'}: "{draftMessage.substring(0, 45)}{draftMessage.length > 45 ? '...' : ''}"</span>
        </div>
      )}

      {/* ✅ Response Length Toggle */}
      <div className="response-preference">
        <button 
          className={`preference-toggle ${shortResponse ? 'active' : ''}`}
          onClick={toggleResponseLength}
        >
          <span className="toggle-icon">{shortResponse ? '⚡' : '📖'}</span>
          <span className="toggle-text">
            {shortResponse ? 'Quick Answers' : 'Detailed Answers'}
          </span>
          <span className="toggle-desc">
            {shortResponse ? 'Short & Simple' : 'Comprehensive'}
          </span>
        </button>
      </div>

      {/* Top Feature Cards */}
      <div className="feature-cards">
        <button className="feature-card kundli" onClick={handleFreeKundli}>
          <div className="feature-title">🔮 PERSONALIZED KUNDLI</div>
          <div className="feature-desc">Your cosmic DNA analysis</div>
        </button>

        <button className="feature-card pooja" onClick={handleBookPooja}>
          <div className="feature-title">📖 SACRED POOJA</div>
          <div className="feature-desc">Divine blessing rituals</div>
        </button>

        <button className="feature-card moon" onClick={handleMoonTracker}>
          <div className="feature-title">🌙 LUNAR GUIDANCE</div>
          <div className="feature-desc">Moon phase insights</div>
        </button>
      </div>

      {/* Main Chat Area */}
      <div className="chat-area">
        <div className="chat-header">
          <span>✨ Welcome {user?.full_name || user?.name || 'seeker'}! Your personalized cosmic journey with Guru ji begins... ✨</span>
        </div>

        <div className="credit-info-bar">
          {messages.length <= 2 ? 
            `🔮 Preparing your ${shortResponse ? 'quick' : 'detailed'} cosmic insights, ${user?.given_name || 'beta'}...` :
            (isFirstQuestion ? 
              '🔮 First divine consultation costs 10 credits' : 
              '💬 Follow-up spiritual guidance costs 5 credits each'
            )
          }
        </div>

        {/* Enhanced Messages Container with Markdown Support */}
        <div className="messages-container">
          {messages.map((msg, idx) => (
            <div key={idx} className={`message-wrapper ${msg.direction}`}>
              <div className={`message-bubble ${msg.direction}`}>
                {/* ReactMarkdown with enhanced styling */}
                <ReactMarkdown
                  components={{
                    strong: ({children}) => (
                      <strong className="md-strong">{children}</strong>
                    ),
                    em: ({children}) => (
                      <em className="md-emphasis">{children}</em>
                    ),
                    h1: ({children}) => (
                      <h1 className="md-h1">{children}</h1>
                    ),
                    h2: ({children}) => (
                      <h2 className="md-h2">{children}</h2>
                    ),
                    h3: ({children}) => (
                      <h3 className="md-h3">{children}</h3>
                    ),
                    p: ({children}) => (
                      <p className="md-paragraph">{children}</p>
                    ),
                    ul: ({children}) => (
                      <ul className="md-list">{children}</ul>
                    ),
                    ol: ({children}) => (
                      <ol className="md-list">{children}</ol>
                    ),
                    li: ({children}) => (
                      <li className="md-list-item">{children}</li>
                    ),
                    blockquote: ({children}) => (
                      <blockquote className="md-blockquote">
                        {children}
                      </blockquote>
                    ),
                    code: ({children}) => (
                      <code className="md-code">
                        {children}
                      </code>
                    ),
                    pre: ({children}) => (
                      <pre className="md-pre">
                        {children}
                      </pre>
                    )
                  }}
                >
                  {msg.message}
                </ReactMarkdown>
              </div>
              
              <div className={`message-timestamp ${msg.direction}`}>
                {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </div>
            </div>
          ))}

          {/* Enhanced Typing Indicator */}
          {isTyping && (
            <div className="typing-indicator">
              <div className="typing-bubble">
                {getRandomGuruMessage()}
              </div>
            </div>
          )}
        </div>

        {/* Enhanced Input Area */}
        <div className="input-area">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder={
              draftMessage 
                ? `Continue your spiritual question, ${user?.given_name || 'beta'}...` 
                : `Ask Guru ji your cosmic question, ${user?.given_name || 'seeker'}...`
            }
            className="chat-input"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isTyping}
            className={`send-button ${inputValue.trim() && !isTyping ? 'active' : 'inactive'}`}
          >
            {isTyping ? '🔮' : 'Send'}
          </button>
        </div>
      </div>

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientModal}
        onClose={handleCloseInsufficientModal}
        requiredCredits={requiredCredits}
        currentCredits={user?.credits || 0}
      />
    </div>
  );
}

export default ChatBot;
